import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { IThunkApi } from '../../timesheet/timesheetSlice';
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI';
import { AttendancePolicyClass } from './interfaces/AttendancePolicyClass';


interface AttendancePolicyState {
    status: 'idle' | 'loading' | 'failed';
    isLoading: boolean;
    currentPolicy: AttendancePolicyClass | null;    
    policyList: AttendancePolicyClass[]; 
    successMessage: string | null;
    error: string | null;
    selectedRecord : Object;
    isReload:boolean;
}


const initialState: AttendancePolicyState = {
    status: 'idle',
    isLoading: false,
    currentPolicy: null,
    policyList: [], 
    successMessage: null,
    error: null,
    selectedRecord : {},    
    isReload: false
};

export const getAttendancePoliciesListAsync = createAsyncThunk<any, void, IThunkApi>(
    'attendancePolicy/GetAttendancePoliciesList',
    async (arg, thunkApi) => {
        try {
            const response = await fetchTimeliveAPI('api/AttendancePolicies/GetAccountAttendancePoliciesList', 'GET', '', true);
            const json = await response.json();
            
            return json;
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);
export const getAttendancePolicyById = createAsyncThunk<any, number, IThunkApi>(
    'attendancePolicy/GetAttendancePolicyById',
    async (accountAttendancePolicyId, thunkApi) => {
        try {
            const response = await fetchTimeliveAPI(`api/AttendancePolicies/GetAttendancePolicyById/${accountAttendancePolicyId}`, 'GET', '', true);
            const json = await response.json();
            return json;
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);

export const updateAttendancePolicyAsync = createAsyncThunk<any, AttendancePolicyClass, IThunkApi>(
    'attendancePolicy/UpdateAttendancePolicy',
    async (arg, thunkApi) => {
        try {
            const id = arg.AccountAttendancePolicyId;
            console.log('@updateAttendancePolicyAsync :',arg)
            const response = await fetchTimeliveAPI(`api/AttendancePolicies/UpdateAccountAttendancePolicy/${id}`, 'PUT', arg, true);
            const json = await response.json();
            return json;
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);

export const activateAttendancePolicyAsync = createAsyncThunk<any, { AccountAttendancePolicyId: number; AttendancePolicyStatusId: number }, IThunkApi>(
    'attendancePolicy/ActivateAttendancePolicy',
    async (arg, thunkApi) => {
        try {
            const id = arg.AccountAttendancePolicyId;
            const response = await fetchTimeliveAPI(`api/AttendancePolicies/ActivateAttendancePolicy/${id}`, 'PUT', arg, true);
            const json = await response.json();
            return json;
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);


export const archiveAttendancePolicyAsync = createAsyncThunk<any, { AccountAttendancePolicyId: number; AttendancePolicyStatusId: number }, IThunkApi>(
    'attendancePolicy/ArchiveAttendancePolicy',
    async (arg, thunkApi) => {
        try {
            const id = arg.AccountAttendancePolicyId;
            const response = await fetchTimeliveAPI(`api/AttendancePolicies/ArchiveAttendancePolicy/${id}`, 'PUT', arg, true);
            const json = await response.json();
            return json;
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);

export const deleteAccountAttendancePolicy = createAsyncThunk
    <any, any, IThunkApi>
    (
        'defaultDashboard/DeleteAccountAttendancePolicy',
        async (arg, thunkApi) => {
            const response = await fetchTimeliveAPI('api/AttendancePolicies/DeleteAccountAttendancePolicy/' + arg, 'DELETE', '', true, 'a73f036e-6d84-440b-85e0-70e157c5eef7');
            const json = await response.json();
            return json;
        }
    )

export const attendancePolicySlice = createSlice({
    name: 'attendancePolicy',
    initialState,
    reducers: {
        clearPolicyStatus: (state) => {
            state.error = null;
            state.successMessage = null;
            state.status = 'idle';
              state.selectedRecord = {};
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(getAttendancePoliciesListAsync.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAttendancePoliciesListAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.isLoading = false;
                state.policyList = action.payload.Data || []; 
            })
            .addCase(getAttendancePoliciesListAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch attendance policies';
            })

            .addCase(getAttendancePolicyById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.currentPolicy = null; 
            })
            .addCase(getAttendancePolicyById.fulfilled, (state, action) => {
                state.isLoading = false;
               state.selectedRecord = action.payload.Data;

            })
            .addCase(getAttendancePolicyById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            
            .addCase(updateAttendancePolicyAsync.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateAttendancePolicyAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.isLoading = false;
                state.successMessage = action.payload?.ResponseMessage || "Policy updated successfully!";
            })
            .addCase(updateAttendancePolicyAsync.rejected, (state, action: any) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.payload?.ResponseMessage || "Failed to update policy.";
            })


            .addCase(activateAttendancePolicyAsync.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
                state.error = null;
            })
            .addCase(activateAttendancePolicyAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.isLoading = false;
                state.successMessage = action.payload?.ResponseMessage || "Policy Activate successfully!";
            })
            .addCase(activateAttendancePolicyAsync.rejected, (state, action: any) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.payload?.ResponseMessage || "Failed to update policy.";
            })


            .addCase(archiveAttendancePolicyAsync.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
                state.error = null;
            })
            .addCase(archiveAttendancePolicyAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.isLoading = false;
                state.successMessage = action.payload?.ResponseMessage || "Policy Archive successfully!";
            })
            .addCase(archiveAttendancePolicyAsync.rejected, (state, action: any) => {
                state.status = 'failed';
                state.isLoading = false;
                state.error = action.payload?.ResponseMessage || "Failed to update policy.";
            })

            .addCase(deleteAccountAttendancePolicy.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteAccountAttendancePolicy.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.isReload = state.isReload ? false : true;
                state.isLoading = false;
            })
            .addCase(deleteAccountAttendancePolicy.rejected, (state) => {
                state.status = 'failed';
            })
    }
});

export const { clearPolicyStatus } = attendancePolicySlice.actions;
export const attendancePolicyState = (state: RootState) => state.attendancePolicy;
export default attendancePolicySlice.reducer;

